import React from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    useTheme,
} from '@mui/material';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import FavoriteOutlinedIcon from '@mui/icons-material/FavoriteOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';

interface Feature {
    icon: React.ReactNode;
    title: string;
    description: string;
    phase: 'before' | 'during' | 'after';
}

const PHASE_COLORS: Record<string, string> = {
    before: '#2D6E6A',
    during: '#C17B3E',
    after:  '#4A6FA5',
};

const PHASE_LABELS: Record<string, string> = {
    before: 'Before Your Trip',
    during: 'During Your Trip',
    after:  'After Your Trip',
};

const FeaturesSection: React.FC = () => {
    const theme = useTheme();

    const features: Feature[] = [
        // BEFORE
        {
            icon: <PeopleOutlinedIcon sx={{ fontSize: '2.2rem' }} />,
            title: 'Trusted Guidance from Your Circle',
            description: 'Friends and family who\'ve been there share real experiences — not generic reviews. Get accurate, personal recommendations from people who actually know you.',
            phase: 'before',
        },
        {
            icon: <VerifiedOutlinedIcon sx={{ fontSize: '2.2rem' }} />,
            title: 'Real Travelers. Real Insights.',
            description: 'Real travelers means your friends and family — not strangers. Their recommendations come with context: your budget, your style, your comfort level.',
            phase: 'before',
        },
        {
            icon: <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: '2.2rem' }} />,
            title: 'Ask Before You Go',
            description: 'Browse trips your circle has already taken to the same destination. See what they loved, what they skipped, and what they wish they\'d known.',
            phase: 'before',
        },
        // DURING
        {
            icon: <PhotoCameraOutlinedIcon sx={{ fontSize: '2.2rem' }} />,
            title: 'Capture Moments as They Happen',
            description: 'Document photos tied to specific trip days and locations. Know exactly who was there, when, and where — context that\'s impossible to reconstruct later.',
            phase: 'during',
        },
        {
            icon: <CalendarMonthOutlinedIcon sx={{ fontSize: '2.2rem' }} />,
            title: 'Your Itinerary, Always at Hand',
            description: 'Day-by-day schedules with every booking and detail organized. Import confirmation PDFs and we\'ll structure everything automatically.',
            phase: 'during',
        },
        {
            icon: <UploadFileOutlinedIcon sx={{ fontSize: '2.2rem' }} />,
            title: 'Share With Your Circle in Real Time',
            description: 'Keep friends and family in the loop as your trip unfolds. Control exactly who sees what — public, friends only, or private.',
            phase: 'during',
        },
        // AFTER
        {
            icon: <FavoriteOutlinedIcon sx={{ fontSize: '2.2rem' }} />,
            title: 'Memories That Last',
            description: 'Trips and photos stay linked forever. Never lose the context of when, where, and who. Your travel story builds over time, one trip at a time.',
            phase: 'after',
        },
        {
            icon: <ShareOutlinedIcon sx={{ fontSize: '2.2rem' }} />,
            title: 'Help Friends Planning the Same Destination',
            description: 'Your experience becomes a trusted resource for your circle. When someone asks "have you been to Lisbon?" — your trip is the answer.',
            phase: 'after',
        },
        {
            icon: <ExploreOutlinedIcon sx={{ fontSize: '2.2rem' }} />,
            title: 'Discover Through People You Trust',
            description: 'Browse the community for inspiration — but filtered through your network first. See what people in your circle have experienced and loved.',
            phase: 'after',
        },
    ];

    const phases: Array<'before' | 'during' | 'after'> = ['before', 'during', 'after'];

    return (
        <Box
            sx={{
                py: { xs: 8, md: 12 },
                background: theme.palette.background.default,
            }}
        >
            <Container maxWidth="lg">
                {/* Section Header */}
                <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontSize: { xs: '2rem', md: '2.75rem' },
                            fontWeight: 'bold',
                            color: 'text.primary',
                            mb: 2,
                        }}
                    >
                        Travel Better, Together
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'text.secondary',
                            maxWidth: '700px',
                            mx: 'auto',
                            fontSize: { xs: '1rem', md: '1.15rem' },
                            lineHeight: 1.6,
                        }}
                    >
                        Percoro supports every stage of the journey — before you go, while you're there, and long after you return.
                    </Typography>
                </Box>

                {/* Features grouped by phase */}
                {phases.map((phase) => (
                    <Box key={phase} sx={{ mb: { xs: 8, md: 10 } }}>
                        {/* Phase label */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                            <Box
                                sx={{
                                    width: 4,
                                    height: 32,
                                    borderRadius: 2,
                                    bgcolor: PHASE_COLORS[phase],
                                }}
                            />
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 700,
                                    color: PHASE_COLORS[phase],
                                    fontSize: { xs: '1.2rem', md: '1.4rem' },
                                }}
                            >
                                {PHASE_LABELS[phase]}
                            </Typography>
                        </Box>

                        <Grid container spacing={4}>
                            {features
                                .filter((f) => f.phase === phase)
                                .map((feature, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                        <Card
                                            sx={{
                                                height: '100%',
                                                borderRadius: '20px',
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
                                                transition: 'all 0.3s ease',
                                                borderTop: `4px solid ${PHASE_COLORS[phase]}`,
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: '0 12px 40px rgba(0,0,0,0.11)',
                                                },
                                            }}
                                        >
                                            <CardContent sx={{ p: 3 }}>
                                                {/* Icon */}
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: '60px',
                                                        height: '60px',
                                                        background: 'transparent',
                                                        border: `2px solid ${PHASE_COLORS[phase]}`,
                                                        borderRadius: '16px',
                                                        mb: 2,
                                                        color: PHASE_COLORS[phase],
                                                    }}
                                                >
                                                    {feature.icon}
                                                </Box>

                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color: 'text.primary',
                                                        mb: 1,
                                                        fontSize: '1.05rem',
                                                    }}
                                                >
                                                    {feature.title}
                                                </Typography>

                                                <Typography
                                                    sx={{
                                                        color: 'text.secondary',
                                                        lineHeight: 1.6,
                                                        fontSize: '0.9rem',
                                                    }}
                                                >
                                                    {feature.description}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                        </Grid>
                    </Box>
                ))}
            </Container>
        </Box>
    );
};

export default FeaturesSection;
